import * as React from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/lib/cjs-interop";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";

const DATEPICKER_Y_TRASITIONS = 100;

const CustomDateInput = React.forwardRef<
  HTMLButtonElement,
  { placeholder?: string; value?: string; onClick?: () => void; className?: string }
>(({ placeholder, className, value, onClick }, ref) => (
  <Button
    ref={ref}
    variant="outline"
    className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground", className)}
    onClick={onClick}
  >
    <CalendarIcon className="mr-2 h-4 w-4" />
    {value || placeholder}
  </Button>
));

CustomDateInput.displayName = "CustomDateInput";

interface DateField {
  key: string;
  label: string;
  is_date: boolean;
}

interface SnapshotDateFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate: Date | null;
  endDate: Date | null;
  selectedField: string | null;
  availableFields: DateField[];
  onDateChange: (startDate: Date | null, endDate: Date | null, selectedField?: string | null) => void;
  onClear: () => void;
}

export const SnapshotDateFilterModal: React.FC<SnapshotDateFilterModalProps> = ({
  open,
  onOpenChange,
  startDate,
  endDate,
  selectedField,
  availableFields,
  onDateChange,
  onClear,
}) => {
  const [tempStartDate, setTempStartDate] = React.useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = React.useState<Date | null>(endDate);
  const [tempSelectedField, setTempSelectedField] = React.useState<string | null>(selectedField);

  // Update temp values when modal opens or props change
  React.useEffect(() => {
    if (open) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setTempSelectedField(selectedField);
    }
  }, [open, startDate, endDate, selectedField]);

  const handleStartDateSelect = (date: Date | null) => {
    setTempStartDate(date);
    // If end date is before start date, clear it
    if (date && tempEndDate && tempEndDate < date) {
      setTempEndDate(null);
    }
  };

  const handleEndDateSelect = (date: Date | null) => {
    setTempEndDate(date);
    // If start date is after end date, clear it
    if (date && tempStartDate && tempStartDate > date) {
      setTempStartDate(null);
    }
  };

  const handleApply = () => {
    onDateChange(tempStartDate, tempEndDate, tempSelectedField);
    onOpenChange(false);
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setTempSelectedField(null);
    onClear();
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset temp values to original values
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setTempSelectedField(selectedField);
    onOpenChange(false);
  };

  const hasChanges = tempStartDate !== startDate || tempEndDate !== endDate || tempSelectedField !== selectedField;
  const hasActiveFilter = tempSelectedField && (tempStartDate || tempEndDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Filter by Date Range</DialogTitle>
          <DialogDescription>
            Select a date field and range to filter snapshots. Leave either date field empty for an open-ended range.
          </DialogDescription>
        </DialogHeader>

        {/* Field Selection */}
        <div className="space-y-3">
          <Label>Date Field</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                {tempSelectedField
                  ? availableFields.find(f => f.key === tempSelectedField)?.label || tempSelectedField
                  : "Select a date field to filter by"}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-full" align="start">
              {availableFields.map(field => (
                <DropdownMenuItem
                  key={field.key}
                  onClick={() => setTempSelectedField(field.key)}
                  className={tempSelectedField === field.key ? "bg-accent" : ""}
                >
                  {field.label}
                  {field.is_date && " (Date)"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Start Date */}
          <div className="space-y-3">
            <Label>Start Date</Label>
            <div className="space-y-2">
              <DatePicker
                selected={tempStartDate}
                onChange={tempSelectedField ? handleStartDateSelect : undefined}
                customInput={<CustomDateInput placeholder="Select start date" className="w-full" />}
                disabled={!tempSelectedField}
                maxDate={tempEndDate || undefined}
                dateFormat="PPP"
                className={cn("w-full", !tempSelectedField && "cursor-not-allowed opacity-50")}
                placeholderText="Select start date"
                isClearable
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                popperPlacement="bottom-start"
                popperModifiers={[{ name: "flip", fn: state => ({ ...state, y: state.y + DATEPICKER_Y_TRASITIONS }) }]}
              />
            </div>
          </div>

          {/* End Date */}
          <div className="space-y-3">
            <Label>End Date</Label>
            <div className="space-y-2">
              <DatePicker
                selected={tempEndDate}
                onChange={tempSelectedField ? handleEndDateSelect : undefined}
                customInput={<CustomDateInput placeholder="Select end date" className="w-full" />}
                disabled={!tempSelectedField}
                minDate={tempStartDate || undefined}
                dateFormat="PPP"
                className={cn("w-full", !tempSelectedField && "cursor-not-allowed opacity-50")}
                placeholderText="Select end date"
                isClearable
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                popperPlacement="bottom-start"
                popperModifiers={[{ name: "flip", fn: state => ({ ...state, y: state.y + DATEPICKER_Y_TRASITIONS }) }]}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <div className="flex w-full items-center justify-between">
            <div>
              {hasActiveFilter && (
                <Button variant="ghost" onClick={handleClear} className="text-destructive">
                  Clear Filter
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleApply} disabled={!hasChanges && !hasActiveFilter}>
                Apply Filter
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
